import { useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { EnvironmentsEventAttributeSummary, Tag } from '@customerio/hydra-ui-api';
import { useFilteredPeopleCount, useUnfilteredPeopleCountOptional } from '@/services/db/customer/queries';
import { useObjectCountOptional } from '@/services/db/object/queries';
import { getWorkspaceTagsQueryOptions } from '@/services/db/tag/queries';
import { getDataIndexAttribute, getDataIndexEvent } from '@/services/db/data-index/queries';
import { dataIndexKeys, type DataIndexAttribute, type DataIndexEvent } from '@/services/db/data-index/entity';
import type { DataIndexItemAddress } from '../urls';
import type { DataIndexUsage } from '../utils/types';
import { objectsCountFilters, peopleCountFilters } from './count-filters';

/**
 * What one item's page reads.
 *
 * An attribute and an event are served by different endpoints and answer in
 * different shapes, so the page reads whichever applies and works in one
 * vocabulary from there.
 */

/** One item, as every part of its page reads it. */
export type DataIndexItemDetail = {
  name: string;
  /** What the person wrote, or nothing. The reserved and generated fallbacks are the caller's. */
  description: string | null;
  /** The description the model wrote, which is shown where there is no written one. */
  generatedDescription: string | null;
  lastSeenAt: number | null;
  lastUsedAt: number | null;
  sources: NonNullable<DataIndexAttribute['metadata']['sources']>;
  tagIds: number[];
  /** 1 = sensitive. Events have no privacy level of their own. */
  privacyLevel: number | null;
  /** How many times the event arrived today. Only an event has one. */
  dailyCount: number | null;
  /** The event's own attributes, newest first. Empty for an attribute. */
  eventAttributes: EnvironmentsEventAttributeSummary[];
  usage: DataIndexUsage;
};

/** The half of the shape both families answer with. */
function sharedDetail(raw: DataIndexAttribute | DataIndexEvent) {
  return {
    name: raw.id,
    description: raw.metadata.description ?? null,
    generatedDescription: raw.metadata.generated_description ?? null,
    lastSeenAt: raw.metadata.last_seen_at ?? null,
    lastUsedAt: raw.metadata.last_used_at ?? null,
    sources: raw.metadata.sources ?? [],
    tagIds: raw.metadata.tag_ids ?? [],
  };
}

function toEventDetail(raw: DataIndexEvent): DataIndexItemDetail {
  return {
    ...sharedDetail(raw),
    privacyLevel: null,
    dailyCount: raw.daily_count,
    // Newest first: an attribute nobody has sent lately is the least
    // interesting thing on the panel.
    eventAttributes: [...raw.attributes].sort((a, b) => (b.last_seen_at ?? 0) - (a.last_seen_at ?? 0)),
    usage: { segments: raw.segments, campaigns: raw.campaigns, newsletters: raw.newsletters },
  };
}

function toAttributeDetail(raw: DataIndexAttribute): DataIndexItemDetail {
  return {
    ...sharedDetail(raw),
    privacyLevel: raw.metadata.privacy_level,
    dailyCount: null,
    eventAttributes: [],
    usage: {
      segments: raw.segments,
      campaigns: raw.campaigns,
      newsletters: raw.newsletters ?? [],
      templates: raw.templates ?? [],
      snippets: raw.snippets ?? [],
      layouts: raw.layouts ?? [],
      design_studio: raw.design_studio ?? [],
    },
  };
}

/** One vocabulary over the two detail shapes. */
export function toItemDetail(raw: DataIndexAttribute | DataIndexEvent): DataIndexItemDetail {
  // Only an event carries a count of how often it arrived today.
  return 'daily_count' in raw ? toEventDetail(raw) : toAttributeDetail(raw);
}

/**
 * The item the URL addresses. Suspends: the whole page is about it.
 *
 * The two families are served by different endpoints, so the read is one
 * query over either — a pair of hooks would mean calling one of them
 * conditionally.
 */
export function useDataIndexItem(workspaceId: number, address: DataIndexItemAddress): DataIndexItemDetail {
  const isEventItem = address.kind === 'event';
  const { scope, name } = address;

  const { data } = useSuspenseQuery<DataIndexAttribute | DataIndexEvent>({
    queryKey: isEventItem
      ? dataIndexKeys.eventDetail(workspaceId, name)
      : dataIndexKeys.attributeDetail(workspaceId, scope, name),
    queryFn: () =>
      isEventItem ? getDataIndexEvent(workspaceId, name) : getDataIndexAttribute(workspaceId, scope, name),
    // Ingestion writes these rows; a cached one can be wrong the moment it shows.
    staleTime: 0,
  });

  return useMemo(() => toItemDetail(data), [data]);
}

/** The tags the item carries, named. */
export function useItemTags(workspaceId: number, tagIds: readonly number[]): Tag[] {
  const { data } = useSuspenseQuery(getWorkspaceTagsQueryOptions(workspaceId));
  return useMemo(() => data.filter((tag) => tagIds.includes(tag.id)), [data, tagIds]);
}

/** How many profiles or objects the item applies to, and how many there are in all. */
export type ItemReach = {
  /** How many carry the item, or null while that is still being counted. */
  withItem: number | null;
  /** How many there are altogether, which the share is taken against. */
  total: number | null;
  /** The list this reach links to, already narrowed to the item. */
  filters: string;
};

export function useItemReach(workspaceId: number, address: DataIndexItemAddress, enabled: boolean): ItemReach {
  const isObject = address.scope.kind !== 'profile';
  const objectTypeId = address.scope.kind === 'profile' ? '' : String(address.scope.objectTypeId);

  const filters = isObject
    ? objectsCountFilters(address.name, Number(objectTypeId))
    : peopleCountFilters(address.kind, address.name);

  const people = useFilteredPeopleCount({ workspaceId, filters, enabled: enabled && !isObject });
  const allPeople = useUnfilteredPeopleCountOptional({ workspaceId, enabled: enabled && !isObject });
  const objects = useObjectCountOptional({ objectTypeId, filters, enabled: enabled && isObject });
  const allObjects = useObjectCountOptional({ objectTypeId, enabled: enabled && isObject });

  return isObject
    ? { withItem: objects.data ?? null, total: allObjects.data ?? null, filters }
    : { withItem: people.data ?? null, total: allPeople.data ?? null, filters };
}
